// Admin control panel at /portal/health/admin/settings (Registrar only,
// handoff Section 9.1). Global module settings backed by health_settings
// (values stored as strings; the PATCH body is Record<string, string>), plus
// the operational view: recent email failures, the suppression list, and the
// audit trail. Facilitators and church leaders never see this page; the
// worker enforces that with a 403 regardless of what the client renders.

import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapsible } from '@cca/ui';
import { healthApi, HealthApiError, type HealthSettings, type OpsResponse } from './api';
import { formatNzDateShort } from './format';

/** Settings as the edit form holds them: everything a string, PATCH-ready. */
type SettingsForm = Record<string, string>;

function toForm(s: HealthSettings): SettingsForm {
  return {
    module_enabled: s.module_enabled ? 'true' : 'false',
    sender: s.sender,
    reply_to: s.reply_to,
    min_n: String(s.min_n),
    min_n_text: String(s.min_n_text),
    min_n_role: String(s.min_n_role),
    divergence_min_n: String(s.divergence_min_n),
    reminder_min_gap_days: String(s.reminder_min_gap_days),
    reminder_max: String(s.reminder_max),
    response_text_retention_days: String(s.response_text_retention_days),
    critical_items: s.critical_items.join(','),
    governance_items: s.governance_items.join(','),
    default_cycle_mode: s.default_cycle_mode,
    auto_reminders: s.auto_reminders,
    edit_reauth: s.edit_reauth,
    ai_model: s.ai_model,
    turnstile_site_key: s.turnstile_site_key,
  };
}

/** Said after the protection state, instead of "not saved yet", while the field
 *  holds the site key the worker refused on the last save (Miranda S7, Jorge). */
const SITE_KEY_REFUSED =
  'The worker refused this site key, so protection is unchanged. A site key is 8 to 60 ' +
  'letters, digits, hyphens or underscores; check it for a space or a stray character.';

/** What the save banner calls a key the worker refused: the two whose form the
 *  worker checks by their labels here, any other by its own name. */
function refusedName(key: string): string {
  if (key === 'turnstile_site_key') return 'the Turnstile site key';
  if (key === 'ai_model') return 'the summarisation model';
  return `the ${key.replace(/_/g, ' ')} setting`;
}

/**
 * The banner after a save. It names each key the worker refused, and it never
 * says "saved" when nothing was (Miranda S7, Jorge).
 */
function saveNotice(updated: number, rejected: readonly string[]): string {
  if (rejected.length === 0) return `Settings saved (${updated} updated).`;
  const refused = new Intl.ListFormat('en-NZ', { type: 'conjunction' }).format(
    rejected.map(refusedName),
  );
  return updated === 0
    ? `Nothing was saved: the worker refused ${refused}.`
    : `Settings saved (${updated} updated), but the worker refused ${refused}.`;
}

/** The fields of an update_settings audit detail, as the worker writes it
 *  (settings.ts); a field with nothing to say is omitted. */
const SETTINGS_AUDIT_FIELDS: ReadonlySet<string> = new Set([
  'updated',
  'rejected',
  'unknown',
  'turnstile_site_key',
]);

/** A setting's name as an audit detail lists it. */
const SETTING_NAME = /^[a-z][a-z0-9_]*$/;

/** A detail field's setting names: none when absent, null when not a list of names. */
function settingNames(value: unknown): string[] | null {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return null;
  const names: string[] = [];
  for (const item of value as unknown[]) {
    if (typeof item !== 'string' || !SETTING_NAME.test(item)) return null;
    names.push(item);
  }
  return names;
}

/**
 * An audit row's Detail as the table shows it. An update_settings detail of
 * exactly the worker's shape (setting names and one marker, never a value)
 * reads in words, so a refused key is never mistaken for a saved one. Anything
 * else (a row from before that shape, which listed the names alone, another
 * action, or text not of that shape) shows exactly as written. Never throws;
 * the result renders as text.
 */
function auditDetailText(action: string, detail: string | null): string {
  const raw = detail ?? '';
  if (action !== 'update_settings') return raw;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return raw;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return raw;
  const fields = parsed as Record<string, unknown>;
  if (Object.keys(fields).some((key) => !SETTINGS_AUDIT_FIELDS.has(key))) return raw;
  const updated = settingNames(fields.updated);
  const rejected = settingNames(fields.rejected);
  const ignored = fields.unknown === undefined ? 0 : fields.unknown;
  const siteKey = fields.turnstile_site_key;
  if (updated === null || rejected === null) return raw;
  if (typeof ignored !== 'number' || !Number.isInteger(ignored) || ignored < 0) return raw;
  if (siteKey !== undefined && siteKey !== 'set' && siteKey !== 'blank') return raw;
  const words: string[] = [];
  if (updated.length > 0) words.push(`Changed: ${updated.join(', ')}.`);
  if (rejected.length > 0) words.push(`Refused: ${rejected.join(', ')}.`);
  if (ignored > 0) words.push(`${ignored} unknown setting${ignored === 1 ? '' : 's'} ignored.`);
  if (siteKey === 'set') words.push('Bot-check site key: set.');
  if (siteKey === 'blank') words.push('Bot-check site key: blanked — the check is off.');
  return words.length > 0 ? words.join(' ') : raw;
}

export function AdminSettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [original, setOriginal] = useState<SettingsForm | null>(null);
  const [ops, setOps] = useState<OpsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [secretConfigured, setSecretConfigured] = useState(false);
  /** The site key the worker refused on the last save, if it refused one. */
  const [refusedSiteKey, setRefusedSiteKey] = useState<string | null>(null);

  // Suppression-list editing (its own state so messages appear by the card,
  // not at the top of a long page).
  const [supEmail, setSupEmail] = useState('');
  const [supBusy, setSupBusy] = useState(false);
  const [supNotice, setSupNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    Promise.all([healthApi.settings(), healthApi.ops()])
      .then(([settingsRes, opsRes]) => {
        const f = toForm(settingsRes.settings);
        setForm(f);
        setOriginal(f);
        setSecretConfigured(settingsRes.turnstile_secret_configured);
        setOps(opsRes);
      })
      .catch((err: unknown) => {
        if (err instanceof HealthApiError && err.status === 401) {
          navigate('/auth/login?returnTo=%2Fportal%2Fhealth%2Fadmin%2Fsettings', { replace: true });
          return;
        }
        setError(
          err instanceof HealthApiError && err.status === 403
            ? 'The settings area is only open to the Registrar.'
            : 'The settings could not be loaded. Please try again.',
        );
      });
  }, [navigate]);

  if (error) {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '30px auto', textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>{error}</p>
      </div>
    );
  }
  if (!form || !original) return <p style={{ color: 'var(--health-grey)' }}>Loading settings...</p>;

  const set = (key: string, value: string): void =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  /** Re-read the operational view after a suppression change. */
  const refreshOps = (): Promise<void> =>
    healthApi
      .ops()
      .then(setOps)
      .catch(() => {
        /* the change itself succeeded; the list refreshes on next load */
      });

  const addSuppression = (): void => {
    const email = supEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSupNotice({ kind: 'err', text: 'Enter a full email address first.' });
      return;
    }
    setSupBusy(true);
    setSupNotice(null);
    healthApi
      .suppressionAdd(email)
      .then(async () => {
        await refreshOps();
        setSupEmail('');
        setSupNotice({ kind: 'ok', text: `${email} will no longer receive any emails.` });
      })
      .catch(() => setSupNotice({ kind: 'err', text: 'That address could not be added.' }))
      .finally(() => setSupBusy(false));
  };

  const removeSuppression = (email: string, reason: string): void => {
    const bounceWarning =
      reason === 'bounced' || reason === 'complained'
        ? ' This address previously bounced or complained, so sending may fail again.'
        : '';
    if (
      !window.confirm(
        `Remove ${email} from the suppression list?\n\nInvitations and reminders will be sent to this address again.${bounceWarning}`,
      )
    ) {
      return;
    }
    setSupBusy(true);
    setSupNotice(null);
    healthApi
      .suppressionRemove(email)
      .then(async () => {
        await refreshOps();
        setSupNotice({ kind: 'ok', text: `${email} can receive emails again.` });
      })
      .catch(async (err: unknown) => {
        if (err instanceof HealthApiError && err.status === 404) {
          await refreshOps(); // already gone — just resync the view
          setSupNotice({ kind: 'ok', text: `${email} was already off the list.` });
        } else {
          setSupNotice({ kind: 'err', text: 'That address could not be removed.' });
        }
      })
      .finally(() => setSupBusy(false));
  };

  const save = (): void => {
    // A pasted site key often carries a space at either end, which the worker
    // refuses, so it is sent trimmed, and the field shows what was sent
    // (Miranda S7, Jorge).
    const sent: SettingsForm = {
      ...form,
      turnstile_site_key: (form.turnstile_site_key ?? '').trim(),
    };
    setForm(sent);
    const patch: Record<string, string> = {};
    for (const [key, value] of Object.entries(sent)) {
      if (original[key] !== value) patch[key] = value;
    }
    if (Object.keys(patch).length === 0) {
      setNotice('Nothing has changed.');
      return;
    }
    setSaving(true);
    setNotice(null);
    healthApi
      .patchSettings(patch)
      .then((res) => {
        // What the worker now holds: the form, except any key it rejected,
        // which keeps its saved value. The bot-protection status reads this, so
        // it never reports a site key the worker refused (Miranda S5, Lazo).
        const saved = { ...sent };
        for (const key of res.rejected) {
          const kept = original[key];
          if (kept !== undefined) saved[key] = kept;
        }
        setOriginal(saved);
        setRefusedSiteKey(
          res.rejected.includes('turnstile_site_key') ? (sent.turnstile_site_key ?? '') : null,
        );
        setNotice(saveNotice(res.updated.length, res.rejected));
      })
      .catch(() => setNotice('The settings could not be saved. Please try again.'))
      .finally(() => setSaving(false));
  };

  // The survey's bot check, in three states (ADR-072 decision 6). The site key
  // is the switch. Set with no secret key on the worker, nobody is turned away
  // but nothing is checked either, and the daily heartbeat reports each such
  // submission, so that state is "on, but not checking", never "off".
  //
  // It reports the SAVED key, not the field: the worker acts on what is saved,
  // so typing changes nothing here until Save, and a typed key not yet saved
  // says so (Miranda S5, Lazo). The status sits just above Save, so its change
  // after a save is the confirmation. Each state wears a shared pill (Miranda
  // P7); off is a registrar's choice, not a fault, so it is neutral. The pill
  // holds the state alone; anything said about it follows as plain text
  // (Miranda P14).
  //
  // A key the worker refused (settings.ts allows 8 to 60 letters, digits,
  // hyphens or underscores) is named as refused while the field still holds it:
  // pressing Save again would only be refused again (Miranda S7, Jorge).
  const savedSiteKey = original.turnstile_site_key ?? '';
  const fieldSiteKey = form.turnstile_site_key ?? '';
  const siteKeyPending = fieldSiteKey !== savedSiteKey;
  const siteKeyRefused = siteKeyPending && fieldSiteKey === refusedSiteKey;
  const protection: { label: string; pill: string; after: string } =
    savedSiteKey === ''
      ? { label: 'Off (no site key)', pill: 'pill-neutral', after: '' }
      : secretConfigured
        ? { label: 'On', pill: 'pill-success', after: '' }
        : {
            label: 'On, but not checking',
            pill: 'pill-warn',
            after: ' (reported in the daily heartbeat)',
          };

  return (
    // minmax(0,1fr): an implicit auto column would inherit the audit table's
    // intrinsic width and stretch the whole page past a phone viewport.
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16, maxWidth: 860 }}>
      <div>
        <h1 style={{ fontSize: 22, marginBottom: 4 }}>Settings</h1>
        <p style={{ fontSize: 13.5, color: 'var(--health-grey)' }}>
          These defaults govern every assessment cycle. Individual cycles can override some of them
          from their own settings tab.
        </p>
      </div>

      {notice && (
        <div className="health-banner health-banner-blue" role="status">
          {notice}
        </div>
      )}

      <SettingsCard title="Module">
        <Field
          label="Module switch"
          hint="When off, links show a closed message and no emails send."
        >
          <select
            className="health-input"
            value={form.module_enabled}
            onChange={(e) => set('module_enabled', e.target.value)}
            aria-label="Module switch"
          >
            <option value="true">On</option>
            <option value="false">Off</option>
          </select>
        </Field>
        <Field label="Default cycle mode">
          <select
            className="health-input"
            value={form.default_cycle_mode}
            onChange={(e) => set('default_cycle_mode', e.target.value)}
            aria-label="Default cycle mode"
          >
            <option value="rostered">Rostered</option>
            <option value="open">Open link</option>
          </select>
        </Field>
        <Field label="Automatic reminders (default)">
          <select
            className="health-input"
            value={form.auto_reminders}
            onChange={(e) => set('auto_reminders', e.target.value)}
            aria-label="Automatic reminders default"
          >
            <option value="off">Off</option>
            <option value="on">On</option>
          </select>
        </Field>
        <Field label="Edit re-authentication (default)">
          <select
            className="health-input"
            value={form.edit_reauth}
            onChange={(e) => set('edit_reauth', e.target.value)}
            aria-label="Edit re-authentication default"
          >
            <option value="off">Off</option>
            <option value="on">On</option>
          </select>
        </Field>
      </SettingsCard>

      <SettingsCard title="Sender">
        <Field label="Sender address">
          <input
            className="health-input"
            type="email"
            value={form.sender ?? ''}
            onChange={(e) => set('sender', e.target.value)}
            aria-label="Sender address"
          />
        </Field>
        <Field label="Reply-to address">
          <input
            className="health-input"
            type="email"
            value={form.reply_to ?? ''}
            onChange={(e) => set('reply_to', e.target.value)}
            aria-label="Reply-to address"
          />
        </Field>
      </SettingsCard>

      <SettingsCard title="Reminders">
        <Field label="Minimum days between reminders">
          <input
            className="health-input health-input-num"
            type="number"
            min={0}
            value={form.reminder_min_gap_days ?? ''}
            onChange={(e) => set('reminder_min_gap_days', e.target.value)}
            aria-label="Minimum days between reminders"
          />
        </Field>
        <Field label="Maximum reminders per person">
          <input
            className="health-input health-input-num"
            type="number"
            min={0}
            value={form.reminder_max ?? ''}
            onChange={(e) => set('reminder_max', e.target.value)}
            aria-label="Maximum reminders per person"
          />
        </Field>
      </SettingsCard>

      <SettingsCard
        title="Anonymity thresholds"
        intro="Below these counts, results stay hidden so no one can be picked out."
      >
        <Field label="Minimum responses before results show (min n)">
          <input
            className="health-input health-input-num"
            type="number"
            min={1}
            value={form.min_n ?? ''}
            onChange={(e) => set('min_n', e.target.value)}
            aria-label="Minimum responses before results show"
          />
        </Field>
        <Field label="Minimum before written answers show">
          <input
            className="health-input health-input-num"
            type="number"
            min={1}
            value={form.min_n_text ?? ''}
            onChange={(e) => set('min_n_text', e.target.value)}
            aria-label="Minimum before written answers show"
          />
        </Field>
        <Field label="Minimum per role for a role breakdown">
          <input
            className="health-input health-input-num"
            type="number"
            min={1}
            value={form.min_n_role ?? ''}
            onChange={(e) => set('min_n_role', e.target.value)}
            aria-label="Minimum per role for a role breakdown"
          />
        </Field>
        <Field label="Minimum responses to note divergence">
          <input
            className="health-input health-input-num"
            type="number"
            min={1}
            value={form.divergence_min_n ?? ''}
            onChange={(e) => set('divergence_min_n', e.target.value)}
            aria-label="Minimum responses to note divergence"
          />
        </Field>
      </SettingsCard>

      <SettingsCard title="Items and retention">
        <Field
          label="Safeguarding (comma-separated sections or ids)"
          hint="Whole sections (s8, or a section's own id) or single question ids (s12c1). Always shown individually and flagged when low; wins over governance where both apply. Instrument versions published with their own designations ignore these lists."
          wide
        >
          <input
            className="health-input"
            type="text"
            value={form.critical_items ?? ''}
            onChange={(e) => set('critical_items', e.target.value)}
            aria-label="Safeguarding sections or item ids"
          />
        </Field>
        <Field
          label="Governance (comma-separated sections or ids)"
          hint="Whole sections (s12) or single question ids. Always shown individually, never alarm-flagged."
          wide
        >
          <input
            className="health-input"
            type="text"
            value={form.governance_items ?? ''}
            onChange={(e) => set('governance_items', e.target.value)}
            aria-label="Governance sections or item ids"
          />
        </Field>
        <Field
          label="Written-answer retention (days after close)"
          hint="Written answers not purged manually are removed after this many days. The 1 to 4 answers are always kept."
        >
          <input
            className="health-input health-input-num"
            type="number"
            min={0}
            value={form.response_text_retention_days ?? ''}
            onChange={(e) => set('response_text_retention_days', e.target.value)}
            aria-label="Written-answer retention days"
          />
        </Field>
        <Field
          label="Summarisation model"
          hint="The model that prepares the written-answer summaries. Change takes effect on the next pass; no redeploy needed."
          wide
        >
          <input
            className="health-input"
            type="text"
            value={form.ai_model ?? ''}
            onChange={(e) => set('ai_model', e.target.value)}
            aria-label="Summarisation model"
          />
        </Field>
      </SettingsCard>

      <SettingsCard title="Bot protection (survey)">
        <Field
          label="Turnstile site key"
          hint="This site key is the switch. When it is set, the survey shows the bot check; when it is blank, the check is off. Add the secret key from the same Turnstile widget to the parishes worker (wrangler secret put TURNSTILE_SECRET_KEY). Without the secret key nobody is turned away, but submissions go through unchecked and the daily heartbeat reports them. This page sees only whether each key is set, not whether they belong together; the daily heartbeat reports either fault."
          wide
        >
          <input
            className="health-input"
            type="text"
            value={form.turnstile_site_key ?? ''}
            onChange={(e) => set('turnstile_site_key', e.target.value)}
            placeholder="0x4AAAAAAA..."
            aria-label="Turnstile site key"
          />
        </Field>
        <p
          className="health-field-wide health-field-hint"
          style={{ color: 'var(--health-grey)', lineHeight: 1.6 }}
        >
          Secret key on the worker:{' '}
          <strong className={`pill ${secretConfigured ? 'pill-success' : 'pill-warn'}`}>
            {secretConfigured ? 'set' : 'not set'}
          </strong>
          . Protection is <strong className={`pill ${protection.pill}`}>{protection.label}</strong>
          {protection.after}.
          {siteKeyRefused
            ? ` ${SITE_KEY_REFUSED}`
            : siteKeyPending && ' Site key not saved yet: press Save settings to apply.'}
        </p>
      </SettingsCard>

      <div className="health-form-actions" style={{ marginTop: 0 }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={save}>
          {saving ? 'Saving...' : 'Save settings'}
        </button>
      </div>

      {/* Operational view */}
      {ops && (
        <>
          <h2 style={{ fontSize: 17, marginTop: 8 }}>Operations</h2>
          <div className="card" style={{ padding: '12px 16px' }}>
            <Collapsible title="Recent email failures and bounces" hint={ops.failures.length}>
              {ops.failures.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>
                  No recent failures. All quiet.
                </p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">When</th>
                        <th scope="col">To</th>
                        <th scope="col">Template</th>
                        <th scope="col">Status</th>
                        <th scope="col">Attempts</th>
                        <th scope="col">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ops.failures.map((f) => (
                        <tr key={f.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {formatNzDateShort(f.created_at)}
                          </td>
                          <td>{f.to_email}</td>
                          <td>{f.template}</td>
                          <td>
                            <span className="pill pill-danger">{f.status}</span>
                          </td>
                          <td>{f.attempts}</td>
                          <td
                            style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {f.error ?? ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Collapsible>
          </div>

          <div className="card" style={{ padding: '12px 16px' }}>
            <h3 style={{ fontSize: 14.5, marginBottom: 8 }}>Suppression list</h3>
            <p style={{ fontSize: 12.5, color: 'var(--health-grey)', marginBottom: 8 }}>
              Addresses that have declined, hard-bounced, or been added here by hand. Invitations
              and reminders always skip them.
            </p>

            {supNotice && (
              <div
                className={`health-banner ${supNotice.kind === 'err' ? 'health-banner-red' : 'health-banner-blue'}`}
                role={supNotice.kind === 'err' ? 'alert' : 'status'}
                style={{ marginBottom: 10 }}
              >
                {supNotice.text}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                flexWrap: 'wrap',
                marginBottom: 12,
              }}
            >
              <input
                className="health-input"
                type="email"
                style={{ width: 260 }}
                placeholder="name@example.com"
                aria-label="Email address to suppress"
                value={supEmail}
                onChange={(e) => setSupEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addSuppression();
                }}
                disabled={supBusy}
              />
              <button
                type="button"
                className="btn btn-sm"
                data-tip="Blocks every invitation and reminder to this address, in every cycle, until it is removed again"
                disabled={supBusy}
                onClick={addSuppression}
              >
                {supBusy ? 'Working...' : 'Add address'}
              </button>
            </div>

            {ops.suppression.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>The list is empty.</p>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">Email</th>
                      <th scope="col">Reason</th>
                      <th scope="col">Since</th>
                      <th scope="col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ops.suppression.map((s) => (
                      <tr key={s.email}>
                        <td>{s.email}</td>
                        <td>{s.reason}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatNzDateShort(s.created_at)}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="instr-ctl instr-ctl-danger"
                            data-tip="Allow emails to this address again"
                            disabled={supBusy}
                            onClick={() => removeSuppression(s.email, s.reason)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card" style={{ padding: '12px 16px' }}>
            <Collapsible title="Audit trail" hint={ops.audit.length}>
              {ops.audit.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--health-grey)' }}>No audit entries yet.</p>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th scope="col">When</th>
                        <th scope="col">Who</th>
                        <th scope="col">Action</th>
                        <th scope="col">Cycle</th>
                        <th scope="col">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ops.audit.map((a, i) => (
                        <tr key={`${a.at}-${i}`}>
                          <td style={{ whiteSpace: 'nowrap' }}>{formatNzDateShort(a.at)}</td>
                          <td>{a.actor}</td>
                          <td>{a.action}</td>
                          <td>{a.cycle_id ?? ''}</td>
                          {/* Wraps, never clips (health.css): a clipped detail
                              once hid "rejected" and showed refused keys as saved. */}
                          <td className="health-audit-detail">
                            {auditDetailText(a.action, a.detail)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Collapsible>
          </div>
        </>
      )}
    </div>
  );
}

function SettingsCard({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="card">
      <h3
        className="health-section-heading"
        style={intro ? { marginBottom: 8, borderBottom: 'none', paddingBottom: 0 } : undefined}
      >
        <span>{title}</span>
      </h3>
      {intro && (
        <p
          style={{
            fontSize: 12.5,
            color: 'var(--health-grey)',
            margin: '0 0 14px',
            paddingBottom: 8,
            borderBottom: '1px solid var(--health-border-soft)',
            lineHeight: 1.6,
          }}
        >
          {intro}
        </p>
      )}
      <div className="health-field-grid">{children}</div>
    </div>
  );
}

function Field({
  label,
  hint,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className={wide ? 'health-field health-field-wide' : 'health-field'}>
      <span className="health-input-label">{label}</span>
      {children}
      {hint && <span className="health-field-hint">{hint}</span>}
    </div>
  );
}
